import {
  KeyboardSensor,
  PointerActivationConstraints,
  PointerSensor
} from '@dnd-kit/dom'
import { useSortable } from '@dnd-kit/react/sortable'
import { Flex, Switch, Typography } from 'antd'
import { GripVertical } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { MouseEvent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { InfoTooltip } from 'src/components/InfoTooltip'
import { useHover, useIconSizes } from 'src/hooks'

import { ColumnPinButtons } from '../ColumnPinButtons/ColumnPinButtons'
import {
  COLUMN_SETTINGS_DND_TYPE,
  ROW_HOVER_CONCEAL_CLASS,
  ROW_HOVER_REVEAL_CLASS
} from '../config'
import type { ColumnPinSide, ColumnSettingsItem } from '../types'

import { useStyles } from './styles'

const ROW_SENSORS = [
  PointerSensor.configure({
    activationConstraints: [
      new PointerActivationConstraints.Distance({ value: 4 })
    ]
  }),
  KeyboardSensor
]

const stopRowClick = (event: MouseEvent) => event.stopPropagation()

type Props = {
  item: ColumnSettingsItem
  index: number
  group: string
  icon?: LucideIcon
  hint?: ReactNode
  isShown: boolean
  isSortable: boolean
  pinSide: ColumnPinSide | undefined
  pinnable?: boolean
  onToggle: (key: string, isShown: boolean) => void
  onPin: (key: string, side: ColumnPinSide | undefined) => void
}

export const ColumnSettingsRow = ({
  item,
  index,
  group,
  icon: Icon,
  hint,
  isShown,
  isSortable,
  pinSide,
  pinnable = true,
  onToggle,
  onPin
}: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()
  const iconSizes = useIconSizes()
  const { isHovered: isActive, hoverProps } = useHover()
  const { ref, handleRef } = useSortable({
    id: item.key,
    index,
    group,
    type: COLUMN_SETTINGS_DND_TYPE,
    accept: COLUMN_SETTINGS_DND_TYPE,
    disabled: !isSortable,
    sensors: ROW_SENSORS
  })

  const swapsIconForGrip = isSortable && Boolean(Icon)

  return (
    <Flex
      ref={ref}
      align="center"
      gap="small"
      className={cx(styles.row, item.isLocked && styles.locked)}
      onClick={item.isLocked ? undefined : () => onToggle(item.key, !isShown)}
      {...hoverProps}
      onDragStartCapture={(event) => event.preventDefault()}
    >
      {isSortable && (
        <GripVertical
          ref={handleRef}
          size={iconSizes.small}
          role="img"
          aria-label={t('column_settings_move')}
          className={cx(
            styles.grip,
            swapsIconForGrip && ROW_HOVER_REVEAL_CLASS
          )}
          onClick={stopRowClick}
        />
      )}

      {Icon && (
        <Icon
          size={iconSizes.small}
          strokeWidth={1.5}
          aria-hidden
          className={cx(
            styles.icon,
            swapsIconForGrip && ROW_HOVER_CONCEAL_CLASS
          )}
        />
      )}

      <Flex align="center" gap="small" className={styles.title}>
        <Typography.Text ellipsis className={styles.titleText}>
          {item.title}
        </Typography.Text>

        {hint && (
          <InfoTooltip title={hint} iconProps={{ onClick: stopRowClick }} />
        )}
      </Flex>

      {pinnable && (isActive || pinSide) && (
        <ColumnPinButtons
          pinSide={pinSide}
          isExpanded={isActive}
          onPin={(side) => onPin(item.key, side)}
        />
      )}

      <Switch
        size="small"
        checked={isShown}
        disabled={item.isLocked}
        aria-label={item.title}
        onClick={(_, event) => event.stopPropagation()}
        onChange={(checked) => onToggle(item.key, checked)}
      />
    </Flex>
  )
}
